import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import normalizeError from '@rsos/sinatra/src/utils/normalizeError';
import {
  fetchMessagesApi,
  createMessageApi,
  updateMessageApi,
  deleteMessageApi,
} from './preconfiguredMessagesAPI';
import {
  PreconfiguredMessagesState,
  PreconfiguredResponse,
  ApiResponse,
  ApiErrorResponse,
} from './types';

/**
 * Redux slice for managing preconfigured messages.
 * Handles CRUD operations and maintains a centralized store of messages.
 *
 * State structure:
 * - messages: Array of preconfigured messages
 * - errors.fetch: Error state for fetch operations
 * - loading.fetch: Loading state for fetch operations
 *
 * Note: CRUD operation states (loading/errors) are handled at the component level
 * to avoid unnecessary global state management.
 */

const initialState: PreconfiguredMessagesState = {
  messages: [],
  loading: {
    fetch: false,
  },
  errors: {
    fetch: { message: null, status: null },
  },
};

/**
 * Fetches all preconfigured messages for an organization.
 * Updates local messages array and manages fetch loading/error states.
 *
 * @param orgName - Organization name
 * @throws {ApiErrorResponse} On API failure
 */
export const fetchMessages = createAsyncThunk<
  ApiResponse,
  string,
  { rejectValue: ApiErrorResponse }
>(
  'preconfiguredMessages/fetchMessages',
  async (orgName, { rejectWithValue }) => {
    try {
      const response = await fetchMessagesApi(orgName);
      return response.data;
    } catch (error) {
      const { message } = normalizeError(error);
      const axiosError = error as AxiosError;
      return rejectWithValue({
        message: message || 'Failed to fetch messages',
        status: axiosError.response?.status || 500,
      });
    }
  },
);

/**
 * Creates a new preconfigured message.
 * Updates local messages array on success.
 * Error handling is delegated to the calling component.
 *
 * @param org - Organization name
 * @param message - Message data to create
 * @throws {ApiErrorResponse} On API failure
 */
export const createMessage = createAsyncThunk<
  PreconfiguredResponse,
  { org: string; message: Partial<PreconfiguredResponse> },
  { rejectValue: ApiErrorResponse }
>(
  'preconfiguredMessages/createMessage',
  async ({ org, message }, { rejectWithValue }) => {
    try {
      const response = await createMessageApi(org, message);
      return response.data;
    } catch (error) {
      const { message } = normalizeError(error);
      const axiosError = error as AxiosError;
      return rejectWithValue({
        message: message || 'Failed to create message',
        status: axiosError.response?.status || 500,
      });
    }
  },
);

/**
 * Updates an existing preconfigured message.
 * Updates the message in local state on success.
 * Error handling is delegated to the calling component.
 *
 * @param org - Organization name
 * @param id - ID of the message to update
 * @param updates - Partial message data to update
 * @throws {ApiErrorResponse} On API failure
 */
export const updateMessage = createAsyncThunk<
  PreconfiguredResponse,
  { org: string; id: string; updates: Partial<PreconfiguredResponse> },
  { rejectValue: ApiErrorResponse }
>(
  'preconfiguredMessages/updateMessage',
  async ({ org, id, updates }, { rejectWithValue }) => {
    try {
      const response = await updateMessageApi(org, id, updates);
      return response.data;
    } catch (error) {
      const { message } = normalizeError(error);
      const axiosError = error as AxiosError;
      return rejectWithValue({
        message: message || 'Failed to update message',
        status: axiosError.response?.status || 500,
      });
    }
  },
);

/**
 * Deletes a preconfigured message by ID.
 * Removes the message from local state on success.
 * Error handling is delegated to the calling component.
 *
 * @param org - Organization name
 * @param id - ID of the message to delete
 * @throws {ApiErrorResponse} On API failure
 */
export const deleteMessage = createAsyncThunk<
  string,
  { org: string; id: string },
  { rejectValue: ApiErrorResponse }
>(
  'preconfiguredMessages/deleteMessage',
  async ({ org, id }, { rejectWithValue }) => {
    try {
      await deleteMessageApi(org, id);
      return id;
    } catch (error) {
      const { message } = normalizeError(error);
      const axiosError = error as AxiosError;
      return rejectWithValue({
        message: message || 'Failed to delete message',
        status: axiosError.response?.status || 500,
      });
    }
  },
);

const preconfiguredMessagesSlice = createSlice({
  name: 'preconfiguredMessages',
  initialState,
  reducers: {
    /**
     * Resets error states either for a specific operation or all operations.
     * @param payload - Error key to reset or 'all' to reset all errors
     */
    resetErrors(state, action: PayloadAction<'fetch' | 'all'>) {
      if (action.payload === 'all') {
        state.errors = initialState.errors;
      } else {
        state.errors[action.payload] = initialState.errors[action.payload];
      }
    },
  },
  extraReducers: builder => {
    builder
      // Fetch messages
      .addCase(fetchMessages.pending, state => {
        state.loading.fetch = true;
        state.errors.fetch = { message: null, status: null };
      })
      .addCase(fetchMessages.fulfilled, (state, action) => {
        state.loading.fetch = false;
        state.messages = action.payload.preconfigured_responses;
      })
      .addCase(fetchMessages.rejected, (state, action) => {
        state.loading.fetch = false;
        state.errors.fetch = {
          message: action.payload?.message || 'Failed to fetch messages',
          status: action.payload?.status || 500,
        };
      })
      // Create message
      .addCase(createMessage.fulfilled, (state, action) => {
        state.messages.push(action.payload);
        state.messages.sort((a, b) => a.title.localeCompare(b.title));
      })
      // Update message
      .addCase(updateMessage.fulfilled, (state, action) => {
        const updatedMsg = action.payload;
        const oldMessage = state.messages.find(msg => msg.id === updatedMsg.id);
        state.messages = state.messages.map(msg =>
          msg.id === updatedMsg.id ? updatedMsg : msg,
        );
        if (oldMessage && oldMessage.title !== updatedMsg.title) {
          state.messages.sort((a, b) => a.title.localeCompare(b.title));
        }
      })
      // Delete message
      .addCase(deleteMessage.fulfilled, (state, action) => {
        state.messages = state.messages.filter(
          msg => msg.id !== action.payload,
        );
      });
  },
});

export const { resetErrors } = preconfiguredMessagesSlice.actions;
export default preconfiguredMessagesSlice.reducer;
